import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { TableCard } from '../../TableCard';
import { useLanguage } from '../../../../contexts/LanguageContext';

// Product analytics for NoticeIA: how big the standalone app is, as the three metrics that
// "lines of code" is usually one word for.
//
// It led with the physical line count, which is what wc -l gives and what nearly every
// badge shows. On this codebase that was about a quarter prose — the comments are long here
// on purpose — so the headline number was measuring the writing as much as the code.
//
// SLOC leads now, and the other two are under it rather than gone. LOC is the reproducible
// one, the number anybody gets by running wc -l, and dropping it would make this figure
// impossible to check against anything. CLOC is not a number to hide either: on the files
// where the reasoning is the point it is a third of the file, which is the intent and not
// an overrun.
type Counts = { loc: number; sloc: number; cloc: number; blank: number };
type Codelines = { total: Counts; byLang: Record<string, Counts> };

export function AnalyticsSection() {
  const { t } = useLanguage();
  const [data, setData] = useState<Codelines | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get<Codelines>('/api/notice/admin/codelines');
      setData(data);
    } catch {
      setError(t('notice.analytics.loadError'));
    } finally {
      setLoaded(true);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <TableCard title={t('notice.analytics.title')} className="flex max-w-4xl flex-col gap-8">

      {!loaded ? (
        <div className="flex justify-center py-8 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : error ? (
        <p className="text-center text-sm text-rose-600">{error}</p>
      ) : data ? (
        <div className="w-full px-4 pb-4 text-center">
          {/* THE WHOLE THING, at the top: every physical line in every source file. It is
              also the only figure here anybody can reproduce — wc -l gives it — which is
              what makes it the one to lead with. What it is MADE of is the ring below. */}
          <div className="text-6xl font-bold tabular-nums text-slate-900">
            {data.total.loc.toLocaleString()}
          </div>
          <div className="mt-2 text-sm font-medium uppercase tracking-wide text-slate-500">
            LOC
          </div>
          <div className="mt-0.5 text-sm text-slate-400">
            {t('notice.analytics.loc')}
          </div>

          {/* The partition, as the shape it is. These three ARE the file: they add to LOC
              exactly and no line is in two of them, which is what makes a pie the honest
              chart here rather than a decoration. A bar chart of the same three would
              invite reading them against each other; a pie says they are one thing
              divided. */}
          <div className="mt-4">
            <Partition total={data.total} t={t} />
          </div>

          {/* Per language, in SLOC: what was actually written, per thing it was written
              in. Under the ring rather than above it, because it divides a DIFFERENT
              total — one slice of the chart, not the whole of it — and putting it beside
              the headline made it read as a breakdown of that. */}
          {Object.keys(data.byLang).length > 0 && (
            <div className="mt-6 border-t border-slate-200 pt-5">
              <div className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                SLOC {t('notice.analytics.byLang')}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {Object.entries(data.byLang)
                  .sort((a, b) => b[1].sloc - a[1].sloc)
                  .map(([lang, n]) => (
                    <span
                      key={lang}
                      className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-600"
                    >
                      <span className="font-mono uppercase">{lang}</span> {n.sloc.toLocaleString()}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </TableCard>
  );
}


// How each slice is drawn. Emerald for the code, because it is the app's colour and the
// figure the panel is about; slate for the prose and the emptiness, which are context
// rather than competitors.
const SLICES = [
  { key: 'sloc', stroke: '#059669', label: 'SLOC', caption: 'notice.analytics.sloc' },
  { key: 'cloc', stroke: '#64748b', label: 'CLOC', caption: 'notice.analytics.cloc' },
  { key: 'blank', stroke: '#cbd5e1', label: 'notice.analytics.blankShort', caption: 'notice.analytics.blank' },
] as const;

// The drawing, in its own coordinates: ring on the left, labels stacked down the right.
//
// SIDE BY SIDE WAS THE MISTAKE, and it is worth naming because the fix looks like a
// styling tweak and is not. An SVG with a viewBox scales its TYPE along with its geometry,
// so the drawing's width decides the font size — and splitting a 450-pixel card into a
// ring with callouts on both sides left each label about 150 pixels to live in, which
// meant type at nine. Putting the labels in one column gives them the card's whole width
// and lets the numbers be numbers.
//
// So these are small on purpose. At 620 across in a card around 450, a unit is about
// three quarters of a pixel: the percentage at 40 lands near 29px, and the caption at 20
// near 15. Read them as "how big against the ring", not as pixels.
const W = 620;
// Tall enough for three boxes of three lines each. The height FOLLOWS the boxes rather
// than the boxes fitting a height that was picked first — a callout that has to fit is a
// callout that sets its own type, and setting type to fit is how the first version of this
// ended up unreadable.
const BOX_H = 158;
const BOX_GAP = 20;
const H = BOX_H * 3 + BOX_GAP * 2 + 34;
const CX = 158;            // the ring sits left, the labels take everything right of it
const CY = H / 2;
const R = 106;             // the ring's centre line, which is what a stroke is drawn on
const RING = 42;
const C = 2 * Math.PI * R;
const BOX_X = 296;
const BOX_W = W - BOX_X - 12;

// Below this the drawing SCROLLS rather than shrinking any further. A horizontal
// scrollbar on a chart is a small cost; a chart nobody can read is the whole cost.
const MIN_PX = 420;

// LOC divided into its three parts, each slice tethered to what it is.
//
// A LEADER LINE rather than a legend underneath. A legend makes the reader carry a colour
// from one place to another and match it, which is work for nothing when a line can say
// it. The lines are drawn BEFORE the arcs, so one leaving a slice on the far side passes
// behind the ring and comes out at its label rather than across the middle of it.
function Partition({ total, t }: {
  total: Counts;
  t: (k: string) => string;
}) {
  const loc = total.loc || 1;
  // Rounded percentages can sum to 99 or 101 — three values rounded independently do —
  // and that is the right trade here. The alternative is fudging the largest slice to
  // make them add up, which prints a number that is not the percentage of anything. The
  // ARCS use the exact fractions, so the picture is always whole even when the labels
  // are a point out.
  const pct = (n: number) => Math.round((n / loc) * 100);

  // Each slice's arc AND the direction of its middle, in one pass: the offset that
  // positions the arc is the same running total the mid-angle is measured from.
  let acc = 0;
  const arcs = SLICES.map((s) => {
    const fraction = total[s.key] / loc;
    // -90° so the ring starts at twelve o'clock, which is where a reader starts.
    const mid = (acc + fraction / 2) * 2 * Math.PI - Math.PI / 2;
    const arc = { offset: acc * C, length: fraction * C };
    acc += fraction;
    // Where the leader leaves the ring: the middle of this slice, on its outer edge.
    return {
      ...s,
      value: total[s.key],
      arc,
      x1: CX + Math.cos(mid) * (R + RING / 2),
      y1: CY + Math.sin(mid) * (R + RING / 2),
    };
  });

  // THE BOXES ARE ORDERED BY HOW HIGH THEIR SLICE IS, not by how big it is.
  //
  // Declaration order put SLOC's box at the top and its anchor at four o'clock, so its
  // leader climbed across the ring while CLOC's and BLANK's came down across it — three
  // lines through one small circle. Sorting by the anchor's height makes every leader
  // travel the same way round, and none of them cross.
  //
  // The cost is that the labels no longer read biggest-first. Worth it: the ring already
  // says which slice is biggest, and it says it better than an ordering does.
  const slices = [...arcs]
    .sort((a, b) => a.y1 - b.y1)
    .map((s, i) => ({
      ...s,
      y: CY + (i - (arcs.length - 1) / 2) * (BOX_H + BOX_GAP),
    }));

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ minWidth: MIN_PX }} className="w-full" role="img">
        {slices.map((s) => (
          <g key={`lead-${s.key}`}>
            {/* A curve, and the control points are what make it read as a pointer: it
                leaves the ring along the slice's own radius and arrives at the box
                horizontally, so both ends say what they are attached to. A straight line
                between the two says neither, and three of them across one small circle
                looked like a scribble. */}
            <path
              d={`M ${s.x1} ${s.y1} C ${s.x1 + (s.x1 - CX) * 0.45} ${s.y1 + (s.y1 - CY) * 0.45}, ${BOX_X - 70} ${s.y}, ${BOX_X} ${s.y}`}
              fill="none"
              stroke={s.stroke}
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            {/* A dot where the curve leaves the ring: without it a leader reads as a
                tangent that happens to touch, rather than as a pointer at one arc. */}
            <circle cx={s.x1} cy={s.y1} r="7" fill={s.stroke} />
          </g>
        ))}

        {/* The arcs over the leaders, so a line from the far side of the ring passes
            behind it. -90° here and not on every point above: rotating the group is one
            transform where rotating each mid-angle would be three. */}
        <g transform={`rotate(-90 ${CX} ${CY})`}>
          {slices.map((s) => (
            <circle
              key={s.key}
              cx={CX}
              cy={CY}
              r={R}
              fill="none"
              stroke={s.stroke}
              strokeWidth={RING}
              strokeDasharray={`${s.arc.length} ${C - s.arc.length}`}
              strokeDashoffset={-s.arc.offset}
            />
          ))}
        </g>

        {slices.map((s) => (
          <g key={`box-${s.key}`}>
            <rect
              x={BOX_X}
              y={s.y - BOX_H / 2}
              width={BOX_W}
              height={BOX_H}
              rx="16"
              fill="none"
              stroke={s.stroke}
              strokeWidth="2.5"
            />
            {/* THREE LINES, which is what the box holds rather than what would fit in it:
                the name, the size, the meaning. The acronym and the percentage were on one
                line and were reading as a single phrase — "SLOC 74%" — which put the same
                weight on a label and on the number the chart exists to show.

                Each is its own <text> so textAnchor centres it independently. One run with
                dx between the parts centres them TOGETHER, which is right for things on a
                line and wrong for things stacked. */}
            <text
              x={BOX_X + BOX_W / 2}
              y={s.y - 44}
              textAnchor="middle"
              className="fill-slate-700 font-mono text-[24px] font-semibold uppercase tracking-wide"
            >
              {s.label.startsWith('notice.') ? t(s.label) : s.label}
            </text>
            <text x={BOX_X + BOX_W / 2} y={s.y + 10} textAnchor="middle">
              <tspan className="fill-slate-900 text-[40px] font-bold tabular-nums">
                {pct(s.value)}%
              </tspan>
              <tspan dx="16" className="fill-slate-500 text-[26px] tabular-nums">
                {s.value.toLocaleString()}
              </tspan>
            </text>
            <text
              x={BOX_X + BOX_W / 2}
              y={s.y + 56}
              textAnchor="middle"
              className="fill-slate-500 text-[20px]"
            >
              {t(s.caption)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
